import { Builder, builder } from '@builder.io/react';
import dynamic from 'next/dynamic';

import codeBlockConfig from 'molecules/CodeBlock/config';
import { imageConfig } from 'molecules/Image/config';
import RichTextBuilderConfig from 'molecules/RichText/config';
import videoConfig from 'molecules/Video/config';
import videoCustomConfig from 'molecules/VideoCustom/config';

import callOutCardConfig from 'organisms/Cards/CallOut/config';
import companyCardConfig from 'organisms/Cards/Company/config';
import quoteCardConfig from 'organisms/Cards/Quote/config';
import resourceConversionPanelConfig from 'organisms/Cards/ResourceConversionPanel/config';
import resourceDownloadCardConfig from 'organisms/Cards/ResourceDownload/config';
import statsCardConfig from 'organisms/Cards/Stats/config';
import transitionConfig from 'organisms/Transition/config';

import accordionConfig from 'components/Accordion/config';
import articleAccordionConfig from 'components/ArticleAccordion/config';
import { awardsConfig } from 'components/Awards/config';
import buildVsBuyQuizConfig from 'components/BuildVsBuyQuiz/config';
import buttonSelectorConfig from 'components/ButtonSelector/config/config';
import calculatorConfig from 'components/Calculator/config';
import capabilityCalloutConfig from 'components/CapabilityCallout/config';
import cardCarouselConfig from 'components/CardCarousel/config';
import bentoBoxCardDeckConfig from 'components/CardDeck/BentoBox/config';
import iconCardDeckConfig from 'components/CardDeck/Icon/config';
import imageCardDeckConfig from 'components/CardDeck/Image/config';
import resourceCardDeckConfig from 'components/CardDeck/Resource/config';
import simpleCardDeckConfig from 'components/CardDeck/Simple/config';
import comparisonCardsConfig from 'components/ComparisonCards/config';
import connectHeroConfig from 'components/ConnectHero/config';
import connectMastheadConfig from 'components/ConnectMasthead/config';
import connectRegistrationConfig from 'components/ConnectRegistration/config';
import containedConversionPanelConfig from 'components/ContainedConversionPanel/config';
import conversionHeroConfig from 'components/ConversionHero/config';
import conversionPanelConfig from 'components/ConversionPanel/config';
import customCardConfig from 'components/CustomCard/config';
import dashboardBackdropConfig from 'components/DashboardBackdrop/config';
import dualFeatureCardsConfig from 'components/DualFeatureCards/config';
import eventExperienceConfig from 'components/EventExperience/config';
import eventOverviewConfig from 'components/EventOverview/config';
import eventRegistrationHeroConfig from 'components/EventRegistrationHero/config';
import featureSpotlightConfig from 'components/FeatureSpotlight/config';
import featuredPartnersConfig from 'components/FeaturedPartners/config';
import featuredSessionsConfig from 'components/FeaturedSessions/config';
import freeTrialFTUXConfig from 'components/FreeTrialFTUX/config';
import { headingConfig } from 'components/Heading/config';
import heroConfig from 'components/Hero/config';
import iFrameConfig from 'components/IFrame/config';
import jumpNavConfig from 'components/JumpNav/config/config';
import landingPageHeroConfig from 'components/LandingPageHero/config';
import { meetTheSpeakersConfig } from 'components/MeetTheSpeakers/config';
import { metricsConfig } from 'components/Metrics/config';
import productExtensionColumnsConfig from 'components/ProductExtensionColumns/config';
import slideboxConfig from 'components/Slidebox/config';
import socialShareConfig from 'components/SocialShare/config';
import speakerCardsConfig from 'components/SpeakerCards/config';
import splitContentSectionConfig from 'components/SplitContentSection/config';
import statsModuleConfig from 'components/StatsModule/config';
import switchbackConfig from 'components/Switchback/config';
import comparisonTableConfig from 'components/Tables/Comparison/config';
import contentTableConfig from 'components/Tables/ContentTable/config';
import featureComparisonTableConfig from 'components/Tables/FeatureComparisonTable/config';
import pricingTableConfigV1 from 'components/Tables/PricingV1/config';
import pricingTableConfigV2 from 'components/Tables/PricingV2/config';
import testimonialsConfig from 'components/Testimonials/config';
import trustbarConfig from 'components/Trustbar/config';
import wistiaFormConfig from 'components/WistiaForm/config';
import wistiaSplitConfig from 'components/WistiaSplit/config';

import listingConfig from 'layout/Listing/config';
import articleConfig from 'layout/article/config';

import type { Component } from '@builder.io/sdk';
import type { FC } from 'react';
import type { ReactChildren } from 'types/global';

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY || '');

builder.setUserAttributes({
  version: 'v1.0.2',
});

const contentEditorBlocks = [
  { component: dynamic(() => import('layout/article')), config: articleConfig },
  { component: dynamic(() => import('components/ArticleAccordion')), config: articleAccordionConfig },
  { component: dynamic(() => import('molecules/RichText')), config: RichTextBuilderConfig },
  { component: dynamic(() => import('molecules/Image/RichTextImage')), config: imageConfig },
  { component: dynamic(() => import('organisms/Cards/CallOut')), config: callOutCardConfig },
  { component: dynamic(() => import('components/Calculator')), config: calculatorConfig },
  { component: dynamic(() => import('molecules/CodeBlock')), config: codeBlockConfig },
  { component: dynamic(() => import('organisms/Cards/Company')), config: companyCardConfig },
  { component: dynamic(() => import('organisms/Cards/Quote')), config: quoteCardConfig },
  { component: dynamic(() => import('organisms/Cards/ResourceDownload')), config: resourceDownloadCardConfig },
  {
    component: dynamic(() => import('organisms/Cards/ResourceConversionPanel')),
    config: resourceConversionPanelConfig,
  },
  { component: dynamic(() => import('organisms/Cards/Stats')), config: statsCardConfig },
  { component: dynamic(() => import('components/Tables/ContentTable')), config: contentTableConfig },
  { component: dynamic(() => import('molecules/Video')), config: videoConfig },
  { component: dynamic(() => import('molecules/VideoCustom')), config: videoCustomConfig },
];

const components = [
  { component: dynamic(() => import('components/Accordion')), config: accordionConfig },
  { component: dynamic(() => import('components/Awards')), config: awardsConfig },
  { component: dynamic(() => import('components/BuildVsBuyQuiz')), config: buildVsBuyQuizConfig },
  { component: dynamic(() => import('components/FreeTrialFTUX')), config: freeTrialFTUXConfig },
  { component: dynamic(() => import('components/ButtonSelector')), config: buttonSelectorConfig },
  { component: dynamic(() => import('components/CapabilityCallout')), config: capabilityCalloutConfig },
  { component: dynamic(() => import('components/CardCarousel')), config: cardCarouselConfig },
  { component: dynamic(() => import('components/CardDeck/BentoBox')), config: bentoBoxCardDeckConfig },
  { component: dynamic(() => import('components/CardDeck/Icon')), config: iconCardDeckConfig },
  { component: dynamic(() => import('components/CardDeck/Image')), config: imageCardDeckConfig },
  { component: dynamic(() => import('components/CustomCard')), config: customCardConfig },
  { component: dynamic(() => import('components/CardDeck/Resource')), config: resourceCardDeckConfig },
  { component: dynamic(() => import('components/CardDeck/Simple')), config: simpleCardDeckConfig },
  { component: dynamic(() => import('components/ConnectHero')), config: connectHeroConfig },
  { component: dynamic(() => import('components/ConnectMasthead')), config: connectMastheadConfig },
  { component: dynamic(() => import('components/ConnectRegistration')), config: connectRegistrationConfig },
  { component: dynamic(() => import('components/ContainedConversionPanel')), config: containedConversionPanelConfig },
  { component: dynamic(() => import('components/ConversionHero')), config: conversionHeroConfig },
  { component: dynamic(() => import('components/ConversionPanel')), config: conversionPanelConfig },
  { component: dynamic(() => import('components/DashboardBackdrop')), config: dashboardBackdropConfig },
  { component: dynamic(() => import('components/ComparisonCards')), config: comparisonCardsConfig },
  { component: dynamic(() => import('components/DualFeatureCards')), config: dualFeatureCardsConfig },
  { component: dynamic(() => import('components/EventExperience')), config: eventExperienceConfig },
  { component: dynamic(() => import('components/EventOverview')), config: eventOverviewConfig },
  { component: dynamic(() => import('components/EventRegistrationHero')), config: eventRegistrationHeroConfig },
  { component: dynamic(() => import('components/FeatureSpotlight')), config: featureSpotlightConfig },
  { component: dynamic(() => import('components/FeaturedPartners')), config: featuredPartnersConfig },
  { component: dynamic(() => import('components/FeaturedSessions')), config: featuredSessionsConfig },
  { component: dynamic(() => import('components/Heading')), config: headingConfig },
  { component: dynamic(() => import('components/Hero')), config: heroConfig },
  { component: dynamic(() => import('components/IFrame')), config: iFrameConfig },
  { component: dynamic(() => import('components/JumpNav')), config: jumpNavConfig },
  { component: dynamic(() => import('components/LandingPageHero')), config: landingPageHeroConfig },
  { component: dynamic(() => import('components/MeetTheSpeakers')), config: meetTheSpeakersConfig },
  { component: dynamic(() => import('components/Metrics')), config: metricsConfig },
  { component: dynamic(() => import('components/ProductExtensionColumns')), config: productExtensionColumnsConfig },
  { component: dynamic(() => import('components/Slidebox')), config: slideboxConfig },
  { component: dynamic(() => import('components/SocialShare')), config: socialShareConfig },
  { component: dynamic(() => import('components/SpeakerCards')), config: speakerCardsConfig },
  { component: dynamic(() => import('components/SplitContentSection')), config: splitContentSectionConfig },
  { component: dynamic(() => import('components/StatsModule')), config: statsModuleConfig },
  { component: dynamic(() => import('components/Switchback')), config: switchbackConfig },
  { component: dynamic(() => import('components/Tables/PricingV1')), config: pricingTableConfigV1 },
  { component: dynamic(() => import('components/Tables/PricingV2')), config: pricingTableConfigV2 },
  { component: dynamic(() => import('components/Tables/Comparison')), config: comparisonTableConfig },
  {
    component: dynamic(() => import('components/Tables/FeatureComparisonTable')),
    config: featureComparisonTableConfig,
  },
  { component: dynamic(() => import('components/Testimonials')), config: testimonialsConfig },
  { component: dynamic(() => import('organisms/Transition')), config: transitionConfig },
  { component: dynamic(() => import('components/Trustbar')), config: trustbarConfig },
  { component: dynamic(() => import('components/WistiaForm')), config: wistiaFormConfig },
  { component: dynamic(() => import('components/WistiaSplit')), config: wistiaSplitConfig },
];

const singleInstance = [{ component: dynamic(() => import('layout/Listing')), config: listingConfig }];

export const allComponents = [...components, ...contentEditorBlocks, ...singleInstance];

/**
 * Defines the menu items that will be registered with the Builder SDK. The `allMenus` array contains objects with a `name` property and a `components` property that references the components to be included in that menu.
 */
const allMenus = [
  { name: 'Rich Text Blocks', components: contentEditorBlocks },
  { name: 'Single Instance', components: singleInstance },
];

/**
 * Registers all the components defined in the `allComponents` array with the Builder SDK.
 * This function is responsible for making the components available for use in the Builder UI.
 */
const generateComponents = () => {
  allComponents.map(component => Builder.registerComponent(component.component, component.config as Component));
};

/**
 * Registers all the menu items defined in the `allMenus` array with the Builder SDK.
 * This function is responsible for making the menu items available for use in the Builder UI.
 */
const generateMenus = () => {
  allMenus.map(menu =>
    Builder.register('insertMenu', {
      name: menu.name,
      items: menu.components.map(component => ({ name: component.config.name })),
    }),
  );
};

export const ComponentGenerator: FC<ReactChildren> = ({ children }) => {
  generateComponents();
  generateMenus();

  return children;
};

export default ComponentGenerator;
